import { useInfiniteQuery } from '@tanstack/react-query';
import { activityService } from '@services/activity';

interface UseActivitiesByOrganizationIdProjectIdParams {
  organizationId?: string;
  projectId?: string | null;
  projectIds?: string[];
  limit?: number;
}

export const useActivitiesByOrganizationIdProjectId = ({
  organizationId,
  projectId,
  projectIds,
  limit = 10,
}: UseActivitiesByOrganizationIdProjectIdParams) => {
  return useInfiniteQuery({
    queryKey: [
      'activities',
      organizationId,
      projectId,
      projectIds ? [...projectIds].sort().join(',') : null,
    ],
    // Cursor pagination: the first page has no cursor; subsequent pages pass the
    // previous page's `nextCursor` as `after`. Keyset is immune to live inserts,
    // so pages never overlap or skip.
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }: any) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const after = pageParam as string | undefined;

      // Priority: projectIds array > single projectId > organization only
      if (projectIds && projectIds.length > 0) {
        return activityService.getActivitiesByOrganizationIdAndProjects(
          organizationId,
          projectIds,
          undefined,
          limit,
          after,
        );
      } else if (projectId) {
        return activityService.getActivitiesByOrganizationIdAndProjects(
          organizationId,
          projectId ? [projectId] : [],
          undefined,
          limit,
          after,
        );
      } else {
        return activityService.getActivitiesByOrganizationId(
          organizationId,
          undefined,
          limit,
          after,
        );
      }
    },
    enabled: !!organizationId,
    getNextPageParam: (lastPage: any) =>
      lastPage?.hasMore ? lastPage?.nextCursor ?? undefined : undefined,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};
